"use client";

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { useAuth } from "@/hooks/use-auth";
import { useIntercom } from "@/hooks/use-intercom";

/**
 * Session-moment beacon: reports what a NEW user is doing right now as
 * Intercom events, so server-side outreach can pick the right moment (and
 * stay silent during the wrong ones, e.g. active media playback).
 *
 * Emits (account age < 7 days only, throttled):
 * - `moment_presence` — heartbeat while the tab is visible (live-now signal)
 * - `moment_route`    — route changes, with ids stripped
 * - `moment_player`   — media playback started/stopped (document-level
 *   capture listeners, so every <audio>/<video> is covered without wiring)
 *
 * No PII beyond the normalized route; Intercom identity is the transport.
 * Kill switch: the `moment-beacon` GrowthBook flag.
 */

const PRESENCE_INTERVAL_MS = 90_000;
const PLAYER_THROTTLE_MS = 30_000;
const MAX_ACCOUNT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Collapse volatile ids so routes group cleanly (hex ids, mongo ids). */
export function normalizePath(pathname: string): string {
  return pathname.replace(/[0-9a-f]{8,}/gi, ":id");
}

export function isNewAccount(createdAt: string | undefined, now = Date.now()): boolean {
  if (!createdAt) return false;
  const ts = Date.parse(createdAt);
  return Number.isFinite(ts) && now - ts < MAX_ACCOUNT_AGE_MS;
}

export function useMomentBeacon(): void {
  const enabled = useFeatureValue("moment-beacon", false);
  const { user } = useAuth();
  const { trackEvent, isActive } = useIntercom();
  const { pathname } = useLocation();

  const lastPresenceRef = useRef(0);
  const lastPlayerRef = useRef(0);

  const eligible = Boolean(enabled && isActive && user && isNewAccount(user.createdAt));

  // Presence heartbeat + visibility recovery
  useEffect(() => {
    if (!eligible) return;

    const beat = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastPresenceRef.current < PRESENCE_INTERVAL_MS - 5_000) return;
      lastPresenceRef.current = now;
      trackEvent("moment_presence", { path: normalizePath(window.location.pathname) });
    };

    beat();
    const interval = window.setInterval(beat, PRESENCE_INTERVAL_MS);
    document.addEventListener("visibilitychange", beat);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", beat);
    };
  }, [eligible, trackEvent]);

  // Route changes
  useEffect(() => {
    if (!eligible) return;
    trackEvent("moment_route", { path: normalizePath(pathname) });
  }, [eligible, pathname, trackEvent]);

  // Media playback state — capture phase catches play/pause from any player
  useEffect(() => {
    if (!eligible) return;

    const onPlay = () => {
      const now = Date.now();
      if (now - lastPlayerRef.current < PLAYER_THROTTLE_MS) return;
      lastPlayerRef.current = now;
      trackEvent("moment_player", { state: "playing", path: normalizePath(window.location.pathname) });
    };
    const onPause = () => {
      lastPlayerRef.current = 0;
      trackEvent("moment_player", { state: "paused", path: normalizePath(window.location.pathname) });
    };

    document.addEventListener("play", onPlay, true);
    document.addEventListener("pause", onPause, true);
    return () => {
      document.removeEventListener("play", onPlay, true);
      document.removeEventListener("pause", onPause, true);
    };
  }, [eligible, trackEvent]);
}
